import React, { useEffect, useState } from 'react'
import {
    Divider,
    Dropdown,
    Form,
    Grid,
    Header,
    Input,
    Modal,
    TextArea,
} from 'semantic-ui-react'
import countries from '../../../../constants/countries.json'

const COUNTRY_OPTIONS = Object.keys(countries).map((name) => ({
    key: name,
    text: name,
    value: name,
}))

const SIZE_OPTIONS = [
    '1-50',
    '51-200',
    '201-500',
    '501-1000',
    '1001-5000',
    '5001-10000',
    '10001+',
].map((size) => ({ key: size, text: size, value: size }))

const SCALAR_FIELDS = ['name', 'url', 'type', 'size', 'description']

const buildInitialFormState = (organization) => ({
    name: organization?.name || '',
    url: organization?.url || '',
    type: organization?.type || '',
    size: organization?.size || '',
    description: organization?.description || '',
    city: organization?.location?.city || '',
    country: organization?.location?.country || '',
})

const buildOrganizationInput = (organization, formState) => {
    const input = {}

    SCALAR_FIELDS.forEach((field) => {
        const original = organization?.[field] || ''
        const next = formState[field] || ''
        if (next !== original) {
            if (field === 'url' && next === '') return
            input[field] = formState[field] || null
        }
    })

    const originalCity = organization?.location?.city || ''
    const originalCountry = organization?.location?.country || ''
    if (
        formState.city !== originalCity ||
        formState.country !== originalCountry
    ) {
        input.location = {
            city: formState.city || originalCity,
            country: formState.country || originalCountry,
        }
    }

    return input
}

const EditOrganizationModal = ({
    organization,
    open,
    onClose,
    onSave,
    saving,
    organizationTypeOptions,
}) => {
    const [formState, setFormState] = useState(() =>
        buildInitialFormState(organization)
    )

    useEffect(() => {
        if (open) {
            setFormState(buildInitialFormState(organization))
        }
    }, [open, organization])

    if (!organization) return null

    const setField = (field) => (e, { value }) =>
        setFormState((current) => ({ ...current, [field]: value }))

    const input = buildOrganizationInput(organization, formState)

    const handleSubmit = (status) => () =>
        onSave(organization, { ...input, status })

    const isCurrentStatus = (status) => organization.status === status

    return (
        <Modal open={open} onClose={onClose} size="small" closeIcon>
            <Modal.Header>Edit Organization</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field>
                        <label>Name</label>
                        <Input
                            value={formState.name}
                            placeholder="Name"
                            onChange={setField('name')}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Website</label>
                        <Input
                            value={formState.url}
                            placeholder="https://..."
                            onChange={setField('url')}
                        />
                    </Form.Field>

                    <Form.Field>
                        <label>Type</label>
                        <Dropdown
                            fluid
                            clearable
                            search
                            selection
                            options={organizationTypeOptions}
                            value={formState.type || null}
                            placeholder="Select type"
                            onChange={(e, { value }) =>
                                setFormState((current) => ({
                                    ...current,
                                    type: value || '',
                                }))
                            }
                        />
                    </Form.Field>

                    <Form.Field>
                        <label>Size</label>
                        <Dropdown
                            fluid
                            clearable
                            selection
                            options={SIZE_OPTIONS}
                            value={formState.size || null}
                            placeholder="Select size"
                            onChange={(e, { value }) =>
                                setFormState((current) => ({
                                    ...current,
                                    size: value || '',
                                }))
                            }
                        />
                    </Form.Field>

                    <Header as="h5" dividing>
                        Location
                    </Header>
                    <Grid columns={2} stackable>
                        <Grid.Column>
                            <Form.Field>
                                <label>City</label>
                                <Input
                                    value={formState.city}
                                    placeholder="City"
                                    onChange={setField('city')}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Form.Field>
                                <label>Country</label>
                                <Dropdown
                                    fluid
                                    clearable
                                    search
                                    selection
                                    options={COUNTRY_OPTIONS}
                                    value={formState.country || null}
                                    placeholder="Country"
                                    onChange={(e, { value }) =>
                                        setFormState((current) => ({
                                            ...current,
                                            country: value || '',
                                        }))
                                    }
                                />
                            </Form.Field>
                        </Grid.Column>
                    </Grid>
                    <Form.Field>
                        <label>Description</label>
                        <TextArea
                            rows={4}
                            value={formState.description}
                            placeholder="Description"
                            onChange={setField('description')}
                        />
                    </Form.Field>
                    <Divider />
                    <Header>Save the data with an organization status</Header>
                    <Form.Button
                        primary
                        fluid
                        basic
                        onClick={handleSubmit('confirmed')}
                        disabled={saving || isCurrentStatus('confirmed')}
                        loading={saving}
                    >
                        Save as "Confirmed"
                    </Form.Button>
                    <Form.Button
                        color="grey"
                        fluid
                        basic
                        onClick={handleSubmit('notFound')}
                        disabled={saving || isCurrentStatus('notFound')}
                        loading={saving}
                    >
                        Save as "Not Found"
                    </Form.Button>
                    <Form.Button
                        color="orange"
                        fluid
                        basic
                        onClick={handleSubmit('irrelevant')}
                        disabled={saving || isCurrentStatus('irrelevant')}
                        loading={saving}
                    >
                       Save as "Irrelevant"
                    </Form.Button>
                    <Form.Button fluid onClick={onClose} disabled={saving}>
                        Cancel
                    </Form.Button>
                </Form>
            </Modal.Content>
        </Modal>
    )
}

export default EditOrganizationModal
